import React from "react";

import {
    BrowserRouter,
    Link,
    Route,
    Routes,
    StaticRouter,
    useNavigate,
} from "react-router";

import * as typography from "@client/shared/typography";

import {ManagerLayout} from "@client/site/ManagerLayout";
import {useMessages, useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {classNames, reverse, server} from "@reactivated";

function ExerciseDetail(props: {
    exerciseType: server.manager.templates.ExerciseMedia["exercise_types"][number];
    onUpdate: (
        updated: server.manager.templates.ExerciseMedia["exercise_types"][number],
    ) => void;
}) {
    const navigate = useNavigate();
    const messages = useMessages();
    const {exerciseType} = props;
    const [isLoading, setIsLoading] = React.useState<null | "query" | "upload">(null);
    const [urls, setUrls] = React.useState<Array<string[]>>([]);
    const [codeLookUp, setCodeLookUp] = React.useState(false);
    const [exerciseMedia, setExerciseMedia] = React.useState({
        query: "",
        gym_visual_code: "",
        gym_visual_image_path: "",
        gym_visual_gif_path: "",
        gym_visual_video_path: "",
        gym_visual_green_video_path: "",
        gym_visual_side: "LEFT" as "LEFT" | "RIGHT",
        no_gif: false,
        no_video: false,
    });

    const form = forms.usePform(
        {
            gym_visual_code: {
                type: "text",
            },
            query: {
                type: "text",
            },
            gym_visual_image_path: {
                type: "text",
            },
            gym_visual_gif_path: {
                type: "text",
            },
            gym_visual_video_path: {
                type: "text",
            },
            gym_visual_green_video_path: {
                type: "text",
            },
            gym_visual_side: {
                type: "select",
                required: true,
                options: [
                    ["LEFT", "Left"],
                    ["RIGHT", "Right"],
                ],
            },
            no_gif: {
                type: "boolean",
            },
            no_video: {
                type: "boolean",
            },
        },
        exerciseMedia,
        setExerciseMedia,
    );

    const imageUrls = urls.filter(
        ([url]) =>
            url.endsWith(".png") && url.includes(codeLookUp == true ? "max" : "small"),
    );
    const gifUrls =
        codeLookUp === true
            ? urls.filter(([url]) => url.endsWith(".gif") && url.includes("1080"))
            : [];

    const videoUrls =
        codeLookUp == true
            ? urls.filter(
                  ([url]) => url.endsWith(".mp4") && url.includes("GREEN") == false,
              )
            : [];

    const greenVideoUrls =
        codeLookUp == true
            ? urls.filter(
                  ([url]) => url.endsWith(".mp4") && url.includes("GREEN") == true,
              )
            : [];

    return (
        <div className="flex gap-4">
            <div className="flex-1 space-y-4">
                <h1 className="text-xl">{exerciseType.name}</h1>
                <form
                    className="space-y-3"
                    onSubmit={async (event) => {
                        event.preventDefault();
                        setIsLoading("query");

                        const result =
                            await server.journal.workouts.exercise_media_lookup({
                                query: form.values.query,
                            });
                        setIsLoading(null);

                        if (result.type == "success") {
                            setCodeLookUp(
                                Number.isNaN(parseInt(exerciseMedia.query)) == false,
                            );
                            setUrls(result.data.urls);
                        }
                    }}
                >
                    <forms.PField field={form.fields.query} />
                    <typography.Button submit disabled={isLoading != null}>
                        {isLoading == "query" ? "Querying..." : "Submit"}
                    </typography.Button>
                </form>

                <form
                    className="space-y-3"
                    onSubmit={async (event) => {
                        event.preventDefault();

                        setIsLoading("upload");

                        await server.journal.workouts.update_exercise_media({
                            ...form.values,
                            exercise_type: {uuid: exerciseType.uuid},
                        });

                        setIsLoading(null);
                        messages.success("Uploaded");
                        props.onUpdate({
                            ...exerciseType,
                            media: {
                                uuid: crypto.randomUUID(),
                            },
                        });
                        void navigate("/");
                    }}
                >
                    <forms.PField field={form.fields.gym_visual_image_path} />
                    <forms.PField field={form.fields.gym_visual_gif_path} />
                    <forms.PField field={form.fields.gym_visual_video_path} />
                    <forms.PField field={form.fields.gym_visual_green_video_path} />
                    <forms.PField field={form.fields.gym_visual_code} />
                    <forms.PField field={form.fields.gym_visual_side} />
                    <forms.PField field={form.fields.no_gif} />
                    <forms.PField field={form.fields.no_video} />
                    <typography.Button
                        submit
                        disabled={
                            isLoading != null ||
                            exerciseMedia.gym_visual_gif_path == "" ||
                            (exerciseMedia.no_gif == false &&
                                exerciseMedia.gym_visual_image_path == "") ||
                            (exerciseMedia.no_video == false &&
                                (exerciseMedia.gym_visual_video_path == "" ||
                                    exerciseMedia.gym_visual_green_video_path == "")) ||
                            exerciseMedia.gym_visual_code == ""
                        }
                    >
                        {isLoading == "upload" ? "Uploading..." : "Upload"}
                    </typography.Button>
                </form>
            </div>
            <div className="h-[80vh] flex-1 space-y-2 overflow-y-scroll">
                <h2 className="text-lg">Images</h2>
                <div className="grid grid-cols-4 gap-2">
                    {imageUrls.map(([url, public_url]) => (
                        <img
                            onClick={() =>
                                setExerciseMedia({
                                    ...exerciseMedia,
                                    gym_visual_image_path: url,
                                })
                            }
                            key={url}
                            className={classNames(
                                "cursor-pointer border-solid border-2",
                                exerciseMedia.gym_visual_image_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                            src={public_url}
                        />
                    ))}
                </div>
                <h2 className="text-lg">Gifs</h2>

                <div className="grid grid-cols-4">
                    {gifUrls.map(([url, public_url]) => (
                        <img
                            onClick={() =>
                                setExerciseMedia({
                                    ...exerciseMedia,
                                    gym_visual_gif_path: url,
                                })
                            }
                            key={url}
                            className={classNames(
                                "cursor-pointer border-solid border-2",
                                exerciseMedia.gym_visual_gif_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                            src={public_url}
                        />
                    ))}
                </div>

                <h2 className="text-lg">Videos</h2>

                <div className="grid grid-cols-4">
                    {videoUrls.map(([url, public_url]) => (
                        <div
                            key={url}
                            className={classNames(
                                "relative border-solid border-2",
                                exerciseMedia.gym_visual_video_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                        >
                            <video autoPlay src={public_url} controls />
                            <div
                                onClick={() =>
                                    setExerciseMedia({
                                        ...exerciseMedia,
                                        gym_visual_video_path: url,
                                    })
                                }
                                className="absolute inset-0 z-10 cursor-pointer"
                            ></div>
                        </div>
                    ))}
                </div>
                <h2 className="text-lg">Green</h2>
                <div className="grid grid-cols-3">
                    {greenVideoUrls.map(([url, public_url]) => (
                        <div
                            key={url}
                            className={classNames(
                                "relative border-solid border-2",
                                exerciseMedia.gym_visual_green_video_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                        >
                            <video autoPlay src={public_url} controls />
                            <div
                                onClick={() =>
                                    setExerciseMedia({
                                        ...exerciseMedia,
                                        gym_visual_green_video_path: url,
                                    })
                                }
                                className="absolute inset-0 z-10 cursor-pointer"
                            ></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
const Router = import.meta.env.SSR == true ? StaticRouter : BrowserRouter;

export const Template = (props: server.manager.templates.ExerciseMedia) => {
    const {request} = useSiteContext();
    const [exerciseTypes, setExerciseTypes] = React.useState(props.exercise_types);

    const basename = reverse("exercise_media");
    const index = (
        <div className="space-y-2">
            <table className="table-fixed">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Count</th>
                        <th>Media</th>
                    </tr>
                </thead>
                <tbody>
                    {exerciseTypes.map((exerciseType) => (
                        <tr key={exerciseType.uuid}>
                            <td>
                                <Link
                                    to={`${exerciseType.uuid}/`}
                                    className="flex gap-2"
                                >
                                    {exerciseType.name}
                                    <div></div>
                                </Link>
                            </td>

                            <td>{exerciseType.instances_count}</td>
                            <td>
                                {exerciseType.media != null && <strong>Yes</strong>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <Router location={request.path} basename={basename}>
            <ManagerLayout title="Exercise media" currentClientManagerId={null}>
                <Routes>
                    <Route path="/" element={index} />
                    {exerciseTypes.map((exerciseType) => (
                        <Route
                            key={exerciseType.uuid}
                            path={`${exerciseType.uuid}/`}
                            element={
                                <ExerciseDetail
                                    exerciseType={exerciseType}
                                    onUpdate={(updated) =>
                                        setExerciseTypes((prevState) =>
                                            prevState.map((item) =>
                                                item.uuid === exerciseType.uuid
                                                    ? updated
                                                    : item,
                                            ),
                                        )
                                    }
                                />
                            }
                        />
                    ))}
                    <Route path="*" element={<div>Not found</div>} />
                </Routes>
            </ManagerLayout>
        </Router>
    );
};
